import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash'
import { connect } from 'react-redux'
import { compose } from 'recompose'

import { notification, modal, logError } from 'redux/modules/app'
import { tasks as tasksApi, awards as awardsApi } from 'redux/modules/api'
import { combineAnd } from 'utils/apiFilter'
import { areAllSelectedForBulk, mapRegistrationsForBulk } from 'redux/selectors/awardRegistrations'

function decorator (config = {}) {
  return ComposedComponent => {
    const enhance = compose(
      connect(
        state => ({
          local: {
            awards: state.api.awards,
            user: state.auth.user,
            activeOrganizationId: state.auth.organization.id,
          },
          tasks: state.api.tasks.registrationParticipants,
          selectableRegistrationIds: config.bulk ? mapRegistrationsForBulk(state) : [],
          areAllRegistrationsSelected: config.bulk ? areAllSelectedForBulk(state) : false,
          commonLimit: state.app.commonLimit
        }),
        {
          ...tasksApi.registrationParticipants,
          getTasksOverview: tasksApi.overview.getTasksOverview,
          approveAwardRegistration: awardsApi.approveAwardRegistration,
          getAward: awardsApi.getAward,
          getAwards: awardsApi.getAwards,
          notification,
          modal,
          logError,
          bulkSelectRegistrations: tasksApi.registrationParticipants.toggleTasksRegistrationAllSelected
        }
      )
    )

    class TasksRegistrationParticipants extends Component {
      static propTypes = {
        decoFilter: PropTypes.string,
        decoFilterMyOnly: PropTypes.bool,
        decoUpdateOrganizationOverview: PropTypes.bool,
        local: PropTypes.object,
        tasks: PropTypes.object.isRequired,
        getTasksParticipantRegistration: PropTypes.func.isRequired,
        refreshTaskParticipantRegistration: PropTypes.func.isRequired,
        exportTasksRegistrationParticipants: PropTypes.func.isRequired,
        getTasksOverview: PropTypes.func.isRequired,
        restoreRemovedRegistration: PropTypes.func.isRequired,
        getRemovedParticipantRegistrations: PropTypes.func.isRequired,
        notification: PropTypes.func.isRequired,
        modal: PropTypes.func.isRequired,
        logError: PropTypes.func.isRequired,
        approveAwardRegistration: PropTypes.func.isRequired,
        getAward: PropTypes.func.isRequired,
        getAwards: PropTypes.func.isRequired,
        setTasksRegistrationSelected: PropTypes.func,
        commonLimit: PropTypes.number,
        setRegistrationTasksParams: PropTypes.func,
        setRemovedRegistrationTasksParams: PropTypes.func
      }

      componentDidMount () {
        let {
          tasks,
          tasks: { removedRegistrations },
        } = this.props
        const {
          commonLimit,
          setRegistrationTasksParams,
          setRemovedRegistrationTasksParams
        } = this.props

        if (config.useCommonLimit && commonLimit) {
          tasks = {
            ...tasks,
            limit: commonLimit
          }
          removedRegistrations = {
            ...removedRegistrations,
            limit: commonLimit
          }

          setRegistrationTasksParams({ limit: commonLimit })
          setRemovedRegistrationTasksParams({ limit: commonLimit })
        }

        config.participants &&
          this.handleGetTasksParticipantRegistration(
            tasks,
            this.handleGetBaseFilter()
          )
        config.removed &&
          this.handleGetRemovedParticipantRegistration(
            removedRegistrations,
            this.handleGetBaseFilter()
          )
      }

      componentWillReceiveProps (nextProps) {
        const {
          tasks,
          tasks: { removedRegistrations }
        } = this.props
        const { filter, limit, offset, sort } = nextProps.tasks
        const {
          filter: filterRemoved,
          limit: limitRemoved,
          offset: offsetRemoved,
          sort: sortRemoved
        } = nextProps.tasks.removedRegistrations

        if (
          tasks.filter !== filter ||
          tasks.limit !== limit ||
          tasks.offset !== offset ||
          tasks.sort !== sort
        ) {
          config.participants &&
            this.handleGetTasksParticipantRegistration(
              nextProps.tasks,
              this.handleGetBaseFilter()
            )
        }

        if (
          removedRegistrations.filter !== filterRemoved ||
          removedRegistrations.limit !== limitRemoved ||
          removedRegistrations.offset !== offsetRemoved ||
          removedRegistrations.sort !== sortRemoved
        ) {
          config.removed &&
            this.handleGetRemovedParticipantRegistration(
              nextProps.tasks.removedRegistrations,
              this.handleGetBaseFilter()
            )
        }
      }

      handleGetBaseFilter = () => {
        const { decoFilterMyOnly } = this.props
        const personId = get(this.props, 'local.user.personId')

        return combineAnd(this.handleGetBaseDecoFilter(),
          decoFilterMyOnly
            ? `award.awardLeader.person.id=${personId}`
            : '')
      }

      handleGetBaseDecoFilter = () => {
        const { params, decoFilter } = this.props

        switch (decoFilter || config.filter) {
          case 'awardUnitRegistrations':
            return `award.organization.id=${params && params.id}`
          case 'removedRegistrations':
            return `organizationId=${params && params.id}`
          case 'organizationParticipantsRegistrations':
            return `or(organization.flatHierarchy.parentOrganization.id=${params &&
            params.id},organization.id=${params && params.id})`
          default:
            return ''
        }
      }

      handleGetTasksParticipantRegistration = (
        tasks,
        baseFilter = this.handleGetBaseFilter(),
        opts
      ) =>
        this.props
          .getTasksParticipantRegistration(tasks, baseFilter, opts)
          .catch(this.handleError)

      handleRefreshTaskParticipantRegistration = (
        taskId
      ) =>
        this.props
          .refreshTaskParticipantRegistration(taskId)
          .catch(this.handleError)

      handleExportParticipantRegistrations = () => {
        const { local } = this.props

        return this.props.exportTasksRegistrationParticipants(
          local.tasks,
          this.handleGetBaseFilter()
        )
      }

      handleGetRemovedParticipantRegistration = (tasks, baseFilter, opts) =>
        this.props
          .getRemovedParticipantRegistrations(tasks, baseFilter, opts)
          .catch(this.handleError)

      handleApproveParticipantRegistration = id =>
        this.props
          .approveAwardRegistration(id, { approved: true })
          .then(() => this.handleSuccess(id))
          .catch(this.handleError)

      handleRestoreParticipantRegistration = (
        participantId,
        registrationId
      ) => {
        this.props
          .restoreRemovedRegistration(participantId, registrationId)
          .then(this.handleRestoreSuccess)
          .catch(this.handleError)
      }
      // this should be in participants reducer
      handleSuccess = awardId => {
        this.props.notification('Participant approved', {
          status: 'success',
          intlKey: 'notifications.participant_has_been_approved'
        })
        this.handleRefresh(awardId)
      }

      handleRestoreSuccess = () => {
        this.props.notification('Participant registration has been restored', {
          status: 'success',
          intlKey: 'notifications.participant_registration_has_been_restored'
        })
        this.handleRefresh()
      }

      handleError = err => {
        console.debug('error', err.body && err.body.userMessage)
        this.props.logError(err)
      }

      handleRefresh = awardId => {
        const {
          tasks,
          tasks: { removedRegistrations },
          params = {},
          local,
          decoUpdateOrganizationOverview
        } = this.props

        config.participants && this.handleGetTasksParticipantRegistration(tasks)

        config.removed &&
          this.handleGetRemovedParticipantRegistration(
            removedRegistrations,
            this.handleGetBaseFilter()
          )

        config.updateAwardDetail && this.props.getAward(params.id)

        if (config.updateParticipantAward) {
          const awardToUpdate = local.awards.list.filter(
            item => item.id === awardId
          )[0]

          get(awardToUpdate, 'awardLevel.value') &&
            get(params, 'id') &&
            this.props.getAwards(
              undefined,
              `participant.id=${params.id},awardLevel.value=${
                awardToUpdate.awardLevel.value
              }`,
              'PAYMENT_INFO,LEADER,PARTICIPANT_REGISTRATION'
            )
        }

        let id = null

        if (decoUpdateOrganizationOverview) {
          id = params.id
        }

        this.props.getTasksOverview(null, {
          orgId: id,
          activeOrganizationId: this.props.local.activeOrganizationId,
          awardLeaderId: this.props.local.user.personId
        })
        this.props.modal(null)
      }

      handleSetRegistrationSelectedTo = (id, newVal) => {
        const ids = this.props.tasks.selectedIds || []
        const newIds = ids.filter(i => i !== id).concat(newVal ? [id] : [])

        this.props.setTasksRegistrationSelected(newIds)
      }

      render () {
        const { tasks } = this.props

        const newProps = {
          ...this.props,
          approveParticipantRegistration: this
            .handleApproveParticipantRegistration,
          onGetTasksParticipantRegistration: opts =>
            this.handleGetTasksParticipantRegistration(
              tasks,
              this.handleGetBaseFilter(),
              opts
            ),
          exportParticipantRegistrations: this
            .handleExportParticipantRegistrations,
          local: undefined
        }

        return (
          <ComposedComponent
            {...newProps}
            onRestoreParticipantRegistration={
              this.handleRestoreParticipantRegistration
            }
            onGetTasksParticipantRegistration={
              this.handleGetTasksParticipantRegistration
            }
            onRefreshTaskParticipantRegistration={
              this.handleRefreshTaskParticipantRegistration
            }
            setRegistrationSelectedTo={this.handleSetRegistrationSelectedTo}
          />
        )
      }
    }

    return enhance(TasksRegistrationParticipants)
  }
}

export default decorator
